import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useSite } from '@/contexts/SiteContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Sparkles,
  Globe,
  CheckCircle2,
  XCircle,
  Loader2,
  ChevronRight,
  ChevronDown,
  Clock,
  Trash2,
  Plus,
  ExternalLink,
  Search,
  Lightbulb
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const MAX_QUESTIONS = 8;

const UNRECOGNIZED_ENTITY_KEYWORDS = [
  'nu există', 'nu am găsit', 'nu e cunoscut', 'nu se referă la',
  'nu am putut găsi', 'nicio', 'no information', 'not aware', "couldn't find"
];

const getCitedDomain = (citations, domain) => {
  for (const c of citations || []) {
    if (!c?.url) continue;
    try {
      let hostname = new URL(c.url).hostname.toLowerCase();
      if (hostname.startsWith('www.')) hostname = hostname.slice(4);
      if (domain && hostname === domain.toLowerCase()) continue;
      return hostname;
    } catch {
      continue;
    }
  }
  return null;
};

const getRemediationTips = (r, domain) => {
  const tips = [];
  const snippet = (r.snippet || '').toLowerCase();
  const isUnrecognizedEntity = UNRECOGNIZED_ENTITY_KEYWORDS.some((kw) => snippet.includes(kw));

  if (isUnrecognizedEntity) {
    tips.push(
      'AI-ul nu recunoaște brandul ca entitate. Creează o intrare Wikidata și un profil LinkedIn Company + Google Business Profile cu exact același nume și descriere.',
      'Obține menționări consecvente pe directoare de business și publicații de nișă — consistența numelui contează mai mult decât volumul.'
    );
  } else if (r.citations?.length > 0) {
    const citedDomain = getCitedDomain(r.citations, domain);
    if (citedDomain) {
      tips.push(
        `Sursa ${citedDomain} a fost citată pentru această întrebare. Creează conținut pe aceeași temă, cu date proprii verificabile (studii de caz, cifre concrete), ca să devii o sursă preferată.`
      );
    }
  }

  tips.push(
    'Verifică indexarea pe Bing (Bing Webmaster Tools) — ChatGPT search se bazează pe indexul Bing, nu doar Google.',
    'Rulează Audit GEO pe această pagină și repară structura de citabilitate (schema FAQ, întrebări ca titluri, răspuns direct în primele propoziții).'
  );

  return tips;
};

export default function AIVisibilityPage() {
  const { currentSite } = useSite();
  const [siteUrl, setSiteUrl] = useState('');
  const [questions, setQuestions] = useState([]);
  const [customQuestion, setCustomQuestion] = useState('');
  const [suggesting, setSuggesting] = useState(false);
  const [checking, setChecking] = useState(false);
  const [checkResult, setCheckResult] = useState(null);
  const [expandedCitations, setExpandedCitations] = useState({});
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);

  useEffect(() => {
    if (currentSite?.site_url) {
      setSiteUrl(currentSite.site_url);
    }
    setQuestions([]);
    setCheckResult(null);
    setExpandedCitations({});
    setCustomQuestion('');
  }, [currentSite]);

  useEffect(() => {
    fetchHistory();
  }, [currentSite]);

  const fetchHistory = async () => {
    try {
      const token = localStorage.getItem('token');
      const params = currentSite?.id ? `?site_id=${currentSite.id}` : '';
      const response = await axios.get(`${API_URL}/api/seo/ai-visibility/history${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHistory(response.data);
    } catch (error) {
      console.error('Error fetching AI visibility history:', error);
    } finally {
      setLoadingHistory(false);
    }
  };

  const suggestQuestions = async () => {
    if (!siteUrl.trim()) {
      toast.error('Introdu un URL valid');
      return;
    }
    setSuggesting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/seo/ai-visibility/suggest-questions`,
        { site_url: siteUrl.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const suggested = (response.data.questions || []).map((text) => ({
        text,
        checked: true
      }));
      setQuestions(suggested);
      toast.success('Întrebări sugerate!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Eroare la generarea întrebărilor');
    } finally {
      setSuggesting(false);
    }
  };

  const addCustomQuestion = () => {
    const text = customQuestion.trim();
    if (!text) return;
    setQuestions((prev) => [...prev, { text, checked: true }]);
    setCustomQuestion('');
  };

  const toggleQuestion = (index) => {
    setQuestions((prev) =>
      prev.map((q, i) => (i === index ? { ...q, checked: !q.checked } : q))
    );
  };

  const removeQuestion = (index) => {
    setQuestions((prev) => prev.filter((_, i) => i !== index));
  };

  const checkedQuestions = questions.filter((q) => q.checked);
  const checkedCount = checkedQuestions.length;
  const runDisabled = checking || checkedCount === 0 || checkedCount > MAX_QUESTIONS;

  const runVisibilityCheck = async () => {
    if (!siteUrl.trim()) {
      toast.error('Introdu un URL valid');
      return;
    }
    if (checkedCount === 0) {
      toast.error('Bifează cel puțin o întrebare');
      return;
    }
    if (checkedCount > MAX_QUESTIONS) {
      toast.error(`Maximum ${MAX_QUESTIONS} întrebări per verificare`);
      return;
    }

    setChecking(true);
    setCheckResult(null);
    setExpandedCitations({});
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/seo/ai-visibility/check`,
        { site_url: siteUrl.trim(), questions: checkedQuestions.map((q) => q.text) },
        { headers: { Authorization: `Bearer ${token}` }, timeout: 120000 }
      );
      setCheckResult(response.data);
      toast.success('Verificare vizibilitate AI finalizată!');
      fetchHistory();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Eroare la verificarea vizibilității AI');
    } finally {
      setChecking(false);
    }
  };

  const toggleCitations = (index) => {
    setExpandedCitations((prev) => ({ ...prev, [index]: !prev[index] }));
  };

  const deleteHistoryItem = async (e, checkId) => {
    e.stopPropagation();
    if (!window.confirm('Ștergi această verificare de vizibilitate AI?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/seo/ai-visibility/${checkId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Verificare ștearsă');
      fetchHistory();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Eroare la ștergere');
    }
  };

  const formatDate = (isoString) => {
    return new Date(isoString).toLocaleString('ro-RO', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl sm:text-3xl font-heading font-bold flex items-center gap-3">
          <Sparkles className="w-8 h-8 text-primary" />
          Vizibilitate AI
        </h1>
        <p className="text-muted-foreground mt-1">
          Verifică dacă site-ul tău apare în răspunsurile AI cu căutare web (ChatGPT)
          {currentSite && <span className="text-primary ml-1">• {currentSite.site_name || currentSite.site_url}</span>}
        </p>
      </div>

      {/* URL Input */}
      <Card className="bg-card border-border">
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <Input
                placeholder="https://exemplu.com"
                value={siteUrl}
                onChange={(e) => setSiteUrl(e.target.value)}
                className="bg-secondary border-border"
              />
            </div>
            <Button
              onClick={suggestQuestions}
              disabled={suggesting}
              className="bg-primary hover:bg-primary/90"
            >
              {suggesting ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Generez...</>
              ) : (
                <><Sparkles className="w-4 h-4 mr-2" />Sugerează întrebări</>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Add custom question */}
      <Card className="bg-card border-border">
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <Input
                placeholder="Scrie propria întrebare..."
                value={customQuestion}
                onChange={(e) => setCustomQuestion(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && addCustomQuestion()}
                className="bg-secondary border-border"
              />
            </div>
            <Button
              onClick={addCustomQuestion}
              variant="outline"
              disabled={!customQuestion.trim()}
            >
              <Plus className="w-4 h-4 mr-2" />
              Adaugă întrebare proprie
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Questions checklist */}
      {questions.length > 0 && (
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="text-base">Întrebări de verificat ({checkedCount} bifate)</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {questions.map((q, idx) => (
              <div key={idx} className="flex items-start gap-3 p-3 bg-secondary/50 rounded-lg">
                <Checkbox
                  checked={q.checked}
                  onCheckedChange={() => toggleQuestion(idx)}
                  className="mt-0.5"
                />
                <span className="text-sm flex-1">{q.text}</span>
                <button
                  onClick={() => removeQuestion(idx)}
                  className="p-1 rounded hover:bg-red-500/20 text-muted-foreground hover:text-red-500 transition-colors flex-shrink-0"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
            <p className="text-xs text-muted-foreground pt-2">
              Cost estimat: ~2-5 cenți per întrebare
            </p>
          </CardContent>
        </Card>
      )}

      {/* Run button */}
      <div className="flex flex-col gap-2">
        <Button
          onClick={runVisibilityCheck}
          disabled={runDisabled}
          className="bg-green-600 hover:bg-green-700 disabled:opacity-50 w-full sm:w-auto"
        >
          {checking ? (
            <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Verific...</>
          ) : (
            <><Search className="w-4 h-4 mr-2" />Rulează Vizibilitate AI</>
          )}
        </Button>
        {checking && (
          <p className="text-sm text-muted-foreground">
            Se verifică {checkedCount} întrebări, poate dura până la 1 minut...
          </p>
        )}
        {!checking && checkedCount > MAX_QUESTIONS && (
          <p className="text-sm text-red-500">
            Maximum {MAX_QUESTIONS} întrebări per verificare — debifează câteva.
          </p>
        )}
      </div>

      {/* Results */}
      {checkResult && (
        <>
          <Card className="bg-card border-border">
            <CardHeader>
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Globe className="w-5 h-5 text-primary" />
                    {checkResult.domain}
                  </CardTitle>
                  <CardDescription>
                    Analizat la {formatDate(checkResult.created_at)}
                  </CardDescription>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-center">
                    <div className="text-3xl font-bold">{checkResult.overall_percent}%</div>
                    <span className="text-sm text-muted-foreground">{checkResult.overall_score}</span>
                  </div>
                </div>
              </div>
            </CardHeader>
          </Card>

          <div className="space-y-3">
            {checkResult.results?.map((r, idx) => (
              <Card key={idx} className="bg-card border-border">
                <CardContent className="pt-6 space-y-3">
                  <div className="flex items-start justify-between gap-4">
                    <span className="text-sm font-medium flex-1">{r.question}</span>
                    {r.found ? (
                      <Badge className="bg-[#00E676] text-black flex items-center gap-1 flex-shrink-0">
                        <CheckCircle2 className="w-3.5 h-3.5" /> Găsit
                      </Badge>
                    ) : (
                      <Badge variant="destructive" className="flex items-center gap-1 flex-shrink-0">
                        <XCircle className="w-3.5 h-3.5" /> Negăsit
                      </Badge>
                    )}
                  </div>
                  {r.snippet && (
                    <p className="text-sm text-muted-foreground">{r.snippet}...</p>
                  )}
                  {r.citations?.length > 0 && (
                    <div>
                      <button
                        onClick={() => toggleCitations(idx)}
                        className="flex items-center gap-1 text-xs text-primary hover:underline"
                      >
                        {expandedCitations[idx] ? (
                          <ChevronDown className="w-3.5 h-3.5" />
                        ) : (
                          <ChevronRight className="w-3.5 h-3.5" />
                        )}
                        {r.citations.length} surse citate
                      </button>
                      {expandedCitations[idx] && (
                        <div className="mt-2 space-y-1.5">
                          {r.citations.map((c, cIdx) => (
                            <a
                              key={cIdx}
                              href={c.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-2 text-xs p-2 bg-secondary/50 rounded hover:bg-secondary/70 transition-colors"
                            >
                              <ExternalLink className="w-3 h-3 flex-shrink-0 text-primary" />
                              <span className="truncate">{c.title || c.url}</span>
                            </a>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                  {!r.found && (
                    <div className="pt-2 border-t border-border">
                      <h4 className="text-sm font-medium text-[#00E676] mb-2 flex items-center gap-2">
                        <Lightbulb className="w-4 h-4" />
                        💡 Cum apari aici?
                      </h4>
                      <div className="space-y-2">
                        {getRemediationTips(r, checkResult.domain).map((tip, tipIdx) => (
                          <div key={tipIdx} className="flex items-start gap-3 p-3 bg-[#00E676]/5 rounded-lg border border-[#00E676]/20">
                            <ChevronRight className="w-4 h-4 text-[#00E676] flex-shrink-0 mt-0.5" />
                            <span className="text-sm">{tip}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </>
      )}

      {/* Istoric */}
      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="w-5 h-5 text-primary" />
            Istoric Verificări
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loadingHistory ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-primary" />
            </div>
          ) : history.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Sparkles className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>Nicio verificare încă. Rulează prima ta verificare de vizibilitate AI!</p>
            </div>
          ) : (
            <div className="space-y-2">
              {history.slice(0, 10).map((item) => (
                <div
                  key={item.id}
                  className="flex items-center justify-between p-3 bg-secondary/50 rounded-lg hover:bg-secondary/70 cursor-pointer transition-colors"
                  onClick={() => setCheckResult(item)}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/20 text-primary flex items-center justify-center">
                      <span className="text-sm font-bold">{item.overall_percent}%</span>
                    </div>
                    <div>
                      <p className="font-medium truncate max-w-[300px]">{item.domain || item.site_url}</p>
                      <p className="text-xs text-muted-foreground">{formatDate(item.created_at)}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <div className="font-bold">{item.overall_score}</div>
                      <div className="text-xs text-muted-foreground">întrebări găsite</div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-muted-foreground" />
                    <button
                      onClick={(e) => deleteHistoryItem(e, item.id)}
                      className="p-1 rounded hover:bg-red-500/20 text-muted-foreground hover:text-red-500 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
